// client/src/pages/SellerDashboard.js (исправлены вызовы загрузки товаров)
import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  registerSeller,
  loginSeller,
  getSellerProducts,
  getSellerOrders,
  getSellerGroups,
  getPricingTerms,
} from '../api';
import api, { logoutSession } from '../api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import useDialogFocus from '../hooks/useDialogFocus';
import SellerOverview from '../components/seller/SellerOverview';
import '../styles/pages/seller.css';
import ProductsTab from '../components/seller/ProductsTab';
import StockTab from '../components/seller/StockTab';
import PricesTab from '../components/seller/PricesTab';
import OrdersTab from '../components/seller/OrdersTab';
import QuestionsTab from '../components/seller/QuestionsTab';
import GroupsTab from '../components/seller/GroupsTab';
import FinanceTab from '../components/seller/FinanceTab';

function SellerDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [seller, setSeller] = useState(() => {
    const saved = localStorage.getItem('seller');
    return saved ? JSON.parse(saved) : null;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');

  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeTab = SELLER_NAV.some((item) => item.key === params.get('tab'))
    ? params.get('tab')
    : 'overview';
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadState, setLoadState] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [authPending, setAuthPending] = useState(false);
  const [stockLoading, setStockLoading] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState(params.get('q') || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => {
      setMobileMenu(media.matches);
      if (!media.matches) setMenuOpen(false);
    };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const menuRef = useDialogFocus(menuOpen && mobileMenu, () =>
    setMenuOpen(false)
  );
  const setActiveTab = (tab, options = {}) => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        'Есть несохранённые изменения. Выйти из раздела и сбросить их?'
      )
    )
      return;
    setHasUnsavedChanges(false);
    setMenuOpen(false);
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', tab);
      next.delete('action');
      Object.entries(options).forEach(([key, value]) => next.set(key, value));
      return next;
    });
  };
  useEffect(() => {
    const warn = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [menuOpen]);

  const [orders, setOrders] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stockProducts, setStockProducts] = useState([]);
  const [stockValues, setStockValues] = useState({});
  const [priceProducts, setPriceProducts] = useState([]);
  const [priceValues, setPriceValues] = useState({});
  const [oldPriceValues, setOldPriceValues] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [answerText, setAnswerText] = useState('');
  const [pricingTerms, setPricingTerms] = useState(null);
  const [pricingError, setPricingError] = useState(false);

  useEffect(() => {
    if (!seller || seller.managedByMaxMarket || activeTab !== 'prices') return;
    let active = true;
    setPricingError(false);
    getPricingTerms().then(({ data }) => { if (active) setPricingTerms(data); })
      .catch(() => { if (active) setPricingError(true); });
    return () => { active = false; };
  }, [seller, activeTab]);

  const calcRetail = (sellerPrice) => {
    if (!pricingTerms) return 0;
    const { acquiringRate, taxRate, commissionRate } = pricingTerms;
    const totalDeductions = acquiringRate + taxRate + commissionRate;
    const divisor = 1 - totalDeductions;
    return Math.floor(sellerPrice / divisor);
  };

  const refreshCatalog = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  }, [queryClient]);

  const loadGroups = useCallback(async () => {
    if (!seller) return;
    try {
      const res = await getSellerGroups(seller.id);
      setGroups(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [seller]);

  const loadProductsWithVariants = useCallback(async () => {
    if (!seller) return;
    setLoadState('loading');
    setLoadError('');
    try {
      const res = await getSellerProducts(seller.id);
      setProducts(res.data);
      setLoadState('ready');
    } catch (err) {
      setLoadState('error');
      setLoadError(
        err.response?.data?.error ||
          'Не удалось загрузить ассортимент. Попробуйте ещё раз.'
      );
    }
  }, [seller]);

  const loadOrders = useCallback(async () => {
    if (!seller) return;
    try {
      const res = await getSellerOrders(seller.id);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [seller]);

  const loadQuestions = useCallback(async () => {
    if (!seller) return;
    try {
      const res = await api.get(`/api/products/seller/${seller.id}/questions`);
      setQuestions(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [seller]);

  const loadStock = useCallback(async () => {
    if (!seller) return;
    setStockLoading(true);
    try {
      const res = await getSellerProducts(seller.id);
      const productsWithVariants = res.data;
      setStockProducts(productsWithVariants);
      const initialStock = {};
      productsWithVariants.forEach((p) => {
        p.variants.forEach((v) => {
          initialStock[v.id] = v.stock;
        });
      });
      setStockValues(initialStock);
    } catch (err) {
      setLoadError(
        'Не удалось загрузить остатки. Вернитесь в раздел и повторите попытку.'
      );
    } finally {
      setStockLoading(false);
    }
  }, [seller]);

  const loadPrices = useCallback(async () => {
    if (!seller) return;
    setPricesLoading(true);
    try {
      const res = await getSellerProducts(seller.id);
      const productsWithVariants = res.data;
      setPriceProducts(productsWithVariants);
      const initialPrices = {};
      const initialOldPrices = {};
      productsWithVariants.forEach((p) => {
        p.variants.forEach((v) => {
          initialPrices[v.id] =
            v.price !== null && v.price !== undefined ? v.price : '';
        });
        initialOldPrices[p.id] = p.oldPrice ?? '';
      });
      setPriceValues(initialPrices);
      setOldPriceValues(initialOldPrices);
      setExpandedProducts({});
    } catch (err) {
      setLoadError(
        'Не удалось загрузить цены. Вернитесь в раздел и повторите попытку.'
      );
    } finally {
      setPricesLoading(false);
    }
  }, [seller]);

  useEffect(() => {
    loadGroups();
    loadProductsWithVariants();
    loadOrders();
    loadQuestions();
  }, [loadGroups, loadProductsWithVariants, loadOrders, loadQuestions]);
  useEffect(() => {
    if (activeTab === 'stock') loadStock();
    if (activeTab === 'prices') loadPrices();
  }, [activeTab, loadStock, loadPrices]);

  const handleAuth = async (event) => {
    event.preventDefault();
    setError('');
    setAuthPending(true);
    try {
      if (!isLogin) await registerSeller({ name, email, password });
      const res = await loginSeller({ email, password });
      const sellerData = res.data.seller;
      localStorage.setItem('seller', JSON.stringify(sellerData));
      localStorage.setItem('sellerAccessToken', res.data.accessToken);
      localStorage.setItem('sellerRefreshToken', res.data.refreshToken);
      setSeller(sellerData);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Не удалось войти. Проверьте данные и попробуйте ещё раз.'
      );
    } finally {
      setAuthPending(false);
    }
  };

  const handleLogout = async () => {
    if (
      hasUnsavedChanges &&
      !window.confirm('Есть несохранённые изменения. Выйти из кабинета?')
    )
      return;
    await logoutSession('seller');
    setSeller(null);
    setProducts([]);
    setOrders([]);
    setHasUnsavedChanges(false);
    navigate('/seller');
  };
  const searchProducts = (event) => {
    event.preventDefault();
    if (
      hasUnsavedChanges &&
      !window.confirm(
        'Сбросить несохранённые изменения и перейти к поиску товаров?'
      )
    )
      return;
    setHasUnsavedChanges(false);
    setParams({
      tab: 'products',
      ...(globalSearch.trim() ? { q: globalSearch.trim() } : {}),
    });
  };

  if (!seller)
    return (
      <div className="seller-auth">
        <div className="seller-auth-intro">
          <Link to="/" className="market-brand">
            <span className="brand-mark">
              <Icon name="bag" />
            </span>
            max<span>market</span>
          </Link>
          <span className="eyebrow">ДЛЯ ПРОДАВЦОВ</span>
          <h1>
            Ваш магазин.
            <br />
            Всё под контролем.
          </h1>
          <p>
            Товары, остатки, заказы и финансы — в одном рабочем пространстве.
          </p>
          <div className="seller-auth-features">
            {[
              ['box', 'Управляйте ассортиментом'],
              ['chart', 'Следите за заказами'],
              ['wallet', 'Проверяйте начисления'],
            ].map(([icon, label]) => (
              <span key={icon}>
                <Icon name={icon} />
                {label}
              </span>
            ))}
          </div>
        </div>
        <section className="seller-auth-card">
          <span className="eyebrow">MAXMARKET SELLER</span>
          <h2>{isLogin ? 'Добро пожаловать' : 'Откройте свой магазин'}</h2>
          <p>
            {isLogin
              ? 'Войдите, чтобы продолжить работу.'
              : 'Создайте аккаунт продавца MaxMarket.'}
          </p>
          {error && (
            <p className="seller-notice seller-notice--error" role="alert">
              {error}
            </p>
          )}
          <form onSubmit={handleAuth}>
            {!isLogin && (
              <label>
                Название магазина
                <input
                  placeholder="Название магазина"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="organization"
                  required
                />
              </label>
            )}
            <label>
              Email
              <input
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label>
              Пароль
              <input
                placeholder="Пароль"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                required
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={authPending}
            >
              {authPending
                ? 'Подождите…'
                : isLogin
                  ? 'Войти'
                  : 'Зарегистрироваться'}
              <Icon name="arrow" size={18} />
            </button>
          </form>
          <p className="seller-auth-switch">
            {isLogin ? 'Ещё нет аккаунта?' : 'Уже есть аккаунт?'}{' '}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
            >
              {isLogin ? 'Зарегистрироваться' : 'Войти'}
            </button>
          </p>
          <Link to="/" className="seller-auth-back">
            ← Вернуться на витрину
          </Link>
        </section>
      </div>
    );

  const currentSection = SELLER_NAV.find((item) => item.key === activeTab);
  return (
    <div className="seller-shell">
      <aside
        ref={menuRef}
        className={`seller-sidebar ${menuOpen ? 'is-open' : ''}`}
        inert={mobileMenu && !menuOpen ? '' : undefined}
        role={mobileMenu ? 'dialog' : undefined}
        aria-label="Меню кабинета"
        aria-modal={mobileMenu && menuOpen ? true : undefined}
        tabIndex={-1}
      >
        <Link to="/" className="market-brand">
          <span className="brand-mark">
            <Icon name="bag" />
          </span>
          max<span>market</span>
        </Link>
        <span className="seller-workspace-label">КАБИНЕТ ПРОДАВЦА</span>
        <div className="seller-shop">
          <span>{seller.name?.slice(0, 1) || 'М'}</span>
          <div>
            <strong>{seller.name}</strong>
            <small>Магазин · FBS</small>
          </div>
        </div>
        <nav aria-label="Разделы кабинета">
          {SELLER_NAV.map((item) => (
            <button
              key={item.key}
              className={activeTab === item.key ? 'active' : ''}
              aria-current={activeTab === item.key ? 'page' : undefined}
              onClick={() => setActiveTab(item.key)}
            >
              <Icon name={item.icon} size={19} />
              <span>{item.label}</span>
              {item.key === 'orders' &&
                orders.filter((o) => o.status === 'PAID').length > 0 && (
                  <b>{orders.filter((o) => o.status === 'PAID').length}</b>
                )}
            </button>
          ))}
        </nav>
        <div className="seller-sidebar-bottom">
          <Link to="/">
            <Icon name="arrow" size={18} />
            Открыть витрину
          </Link>
          <button onClick={handleLogout}>
            <Icon name="logout" size={18} />
            Выйти
          </button>
        </div>
      </aside>
      {menuOpen && (
        <button
          className="seller-menu-overlay"
          aria-label="Закрыть меню кабинета"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <div className="seller-workspace">
        <header className="seller-topbar">
          <button
            className="seller-mobile-menu"
            aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню кабинета'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} />
          </button>
          <div className="seller-breadcrumb">
            Магазин <Icon name="chevron" size={14} />
            <strong>{currentSection.label}</strong>
          </div>
          <form
            className="seller-global-search"
            role="search"
            onSubmit={searchProducts}
          >
            <Icon name="search" size={18} />
            <input
              aria-label="Поиск по ассортименту"
              placeholder="Название товара или артикул"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
            <button aria-label="Найти в ассортименте">
              <Icon name="arrow" size={16} />
            </button>
          </form>
          <span className="seller-topbar-avatar" title={seller.name}>
            {seller.name?.slice(0, 1)}
          </span>
        </header>
        <main className="seller-main" id="seller-content">
          {loadError && (
            <div role="alert" className="seller-notice seller-notice--error">
              {loadError}
              <button onClick={loadProductsWithVariants}>Повторить</button>
            </div>
          )}
          {activeTab === 'overview' && (
            <SellerOverview
              seller={seller}
              products={products}
              orders={orders}
              loading={loadState === 'loading'}
              onNavigate={setActiveTab}
            />
          )}
          {activeTab === 'products' && (
            <ProductsTab
              seller={seller}
              products={products}
              groups={groups}
              loading={loadState === 'loading'}
              loadGroups={loadGroups}
              loadProductsWithVariants={loadProductsWithVariants}
              calcRetail={calcRetail}
            />
          )}
          {activeTab === 'groups' && (
            <GroupsTab
              seller={seller}
              groups={groups}
              loadGroups={loadGroups}
            />
          )}
          {activeTab === 'stock' && (
            <StockTab
              loading={stockLoading}
              stockProducts={stockProducts}
              stockValues={stockValues}
              setStockValues={setStockValues}
              onDataChanged={() => {
                refreshCatalog();
                loadProductsWithVariants();
              }}
              seller={seller}
              onDirtyChange={setHasUnsavedChanges}
            />
          )}
          {activeTab === 'prices' && seller.managedByMaxMarket && (
            <section className="seller-panel"><h2>Цены</h2><p>Закупочные и витринные цены настраивает администратор MaxMarket. Начисления по заказам доступны в разделе «Финансы».</p></section>
          )}
          {activeTab === 'prices' && !seller.managedByMaxMarket && !pricingTerms && (
            <section className="seller-panel"><p role="status">{pricingError ? 'Условия расчёта недоступны. Обновите страницу.' : 'Загружаем условия расчёта цены…'}</p></section>
          )}
          {activeTab === 'prices' && !seller.managedByMaxMarket && pricingTerms && (
            <PricesTab
              loading={pricesLoading}
              priceProducts={priceProducts}
              priceValues={priceValues}
              setPriceValues={setPriceValues}
              oldPriceValues={oldPriceValues}
              setOldPriceValues={setOldPriceValues}
              expandedProducts={expandedProducts}
              setExpandedProducts={setExpandedProducts}
              calcRetail={calcRetail}
              seller={seller}
              loadPrices={loadPrices}
              onDataChanged={() => {
                refreshCatalog();
                loadProductsWithVariants();
              }}
              onDirtyChange={setHasUnsavedChanges}
            />
          )}
          {activeTab === 'orders' && (
            <OrdersTab sellerId={seller.id} initialOrders={orders} />
          )}
          {activeTab === 'finance' && <FinanceTab managedByMaxMarket={seller.managedByMaxMarket} />}
          {activeTab === 'questions' && (
            <QuestionsTab
              products={products}
              questions={questions}
              answerText={answerText}
              setAnswerText={setAnswerText}
              loadQuestions={loadQuestions}
            />
          )}
        </main>
        <footer className="seller-footer">
          <span>MaxMarket Seller</span>
          <span>Ваш магазин. Ваш следующий шаг.</span>
        </footer>
      </div>
    </div>
  );
}

const SELLER_NAV = [
  { key: 'overview', label: 'Обзор магазина', icon: 'grid' },
  { key: 'products', label: 'Товары', icon: 'box' },
  { key: 'stock', label: 'Остатки', icon: 'layers' },
  { key: 'prices', label: 'Цены', icon: 'tag' },
  { key: 'orders', label: 'Заказы', icon: 'bag' },
  { key: 'finance', label: 'Финансы', icon: 'wallet' },
  { key: 'groups', label: 'Группы товаров', icon: 'layers' },
  { key: 'questions', label: 'Вопросы', icon: 'message' },
];
export default SellerDashboard;
